import { Link, useNavigate } from "@tanstack/react-router";
import { MenuIcon, CloseIcon, LogOutIcon, LayoutDashboardIcon, LogoMark } from "@/components/ui/TriIcons";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { brand } from "@/lib/brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";

import { products } from "@/lib/product-data";

const FOREST = "#0C4A6E";
const FOREST_DEEP = "#082F49";
const FOREST_MID = "#0EA5E9";
const FOREST_TINT = "#E0F2FE";
const SILVER = "#94A3B8";
const SILVER_LIGHT = "#F0F9FF";
const VERMILION = "#DC2626";
const GOLD = "#F59E0B";
const INK = "#0C4A6E";
const PAPER = "#F0F9FF";
const SURFACE = "#FFFFFF";
const LINE = "#BAE6FD";
const MUTED = "#475569";
const BG_BASE = "#F0F9FF";
const BG_SURFACE = "#FFFFFF";
const BG_SUBTLE = "#E0F2FE";
const TEXT_PRIMARY = "#0C4A6E";
const TEXT_MUTED = "#475569";
const BORDER = "#BAE6FD";
const FONT_DISPLAY = "'Montserrat', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
const EASE_FOOTER = [0.16, 1, 0.3, 1] as const;

const sectionLinks: { href: string; label: string; accent?: string }[] = [
  { href: "#loop", label: "The Loop", accent: "#0C4A6E" },
  { href: "#rails", label: "Platform", accent: "#0EA5E9" },
  { href: "#school-in-a-box", label: "School-in-a-Box", accent: "#14B8A6" },
];

const mobileLinks: { href: string; label: string; accent?: string }[] = [
  { href: "#loop", label: "The Loop", accent: "#0C4A6E" },
  { href: "#rails", label: "Platform", accent: "#0EA5E9" },
  { href: "#school-in-a-box", label: "School-in-a-Box", accent: "#14B8A6" },
];

function BrandMark({ size = "md", logoUrl }: { size?: "sm" | "md"; logoUrl?: string | null }) {
  const dim = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  const src = logoUrl || brand.logoUrl;
  if (src) {
    return (
      <img
        src={src}
        alt={`${brand.name} logo`}
        className={`${dim} rounded-[10px] object-contain`}
      />
    );
  }
  return (
    <div className={`relative ${dim} shrink-0`}>
      <div
        className="absolute inset-0 rounded-[11px]"
        style={{
          background: FOREST,
        }}
      />
      <LogoMark
        className="absolute inset-0 m-auto h-5 w-5"
        accent={SILVER_LIGHT}
        style={{ color: "rgba(255,255,255,0.92)" }}
      />
    </div>
  );
}

export function Navbar({
  brandName = brand.name,
  tagline = brand.tagline,
  logoUrl = null,
}: {
  brandName?: string;
  tagline?: string;
  logoUrl?: string | null;
} = {}) {
  const { user, profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);

      // Detect which section is under the navbar (at ~40px from top)
      const probe = 40;
      const el = document.elementFromPoint(window.innerWidth / 2, probe);
      let target: HTMLElement | null = el as HTMLElement;
      while (target && target.tagName !== "SECTION") {
        target = target.parentElement;
      }
      if (target) {
        const bg = window.getComputedStyle(target).backgroundColor;
        // Parse rgb(r, g, b) or rgba(r, g, b, a)
        const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (m) {
          const [r, g, b] = [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])];
          // Relative luminance — dark backgrounds need light text
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          setDarkMode(lum < 0.35);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/" });
  };

  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? "U";

  const roleLabel = profile?.role === "school_admin" ? "School Admin"
    : profile?.role === "superadmin" ? "Admin"
    : profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1)
    : "User";

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="fixed top-4 left-0 right-0 z-50 flex justify-center px-4"
      >
        <motion.nav
          animate={{
            paddingTop: scrolled ? 8 : 12,
            paddingBottom: scrolled ? 8 : 12,
          }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="flex w-full max-w-[1280px] items-center justify-between rounded-2xl border px-4 sm:px-5"
          style={{
            borderColor: darkMode
              ? "rgba(255,255,255,0.12)"
              : scrolled ? "rgba(12,74,110,0.12)" : "rgba(12,74,110,0.06)",
            background: darkMode
              ? "rgba(8,47,73,0.82)"
              : scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.80)",
            backdropFilter: "blur(20px) saturate(160%)",
            WebkitBackdropFilter: "blur(20px) saturate(160%)",
            boxShadow: scrolled ? "0 4px 24px -4px rgba(12,74,110,0.10)" : "none",
          }}
          onMouseLeave={() => setHoveredGroup(null)}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark logoUrl={logoUrl} />
            <div className="leading-tight">
              <div className="text-lg font-bold tracking-tight" style={{ color: darkMode ? "#fff" : INK, fontFamily: FONT_DISPLAY }}>{brandName}</div>
              <div className="text-[8px] uppercase tracking-[0.2em] font-semibold" style={{ color: darkMode ? "rgba(255,255,255,0.5)" : FOREST_MID }}>
                {tagline}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setHoveredGroup(null)}>
            {sectionLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="group relative rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors"
                style={{ color: darkMode ? "rgba(255,255,255,0.7)" : MUTED }}
                onMouseEnter={() => setHoveredGroup(link.href)}
              >
                <span
                  className="absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{ background: darkMode ? "rgba(255,255,255,0.06)" : `${link.accent}14` }}
                />
                <span
                  className="absolute bottom-1 left-1/2 h-0.5 -translate-x-1/2 rounded-full transition-all duration-300 group-hover:w-5"
                  style={{ background: link.accent, width: hoveredGroup === link.href ? "20px" : "0px" }}
                />
                <span className="relative z-10">{link.label}</span>
              </a>
            ))}
          </div>

          {/* Right side actions */}
          <div className="hidden items-center gap-2 lg:flex">
            <button
              className="grid h-9 w-9 place-items-center rounded-full transition"
              style={{ color: darkMode ? "rgba(255,255,255,0.7)" : undefined }}
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 text-sm transition" style={{ background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)", color: darkMode ? "#fff" : INK }}>
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-[#0C4A6E] text-xs text-white">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="max-w-[100px] truncate">{profile?.full_name?.split(" ")[0] ?? "User"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    {user.email}
                    <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px]">{roleLabel}</span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/app" className="flex items-center gap-2">
                      <LayoutDashboardIcon className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOutIcon className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="rounded-full">
                  <Link to="/auth" style={{ color: darkMode ? "#fff" : undefined }}>Sign in</Link>
                </Button>
                <a href="#audit" className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition-all hover:opacity-90" style={{ background: "#0C4A6E", color: "#F0F9FF" }}>
                  Request audit
                </a>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" style={{ color: darkMode ? "#fff" : INK }}>
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </motion.nav>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 h-full w-80 max-w-[85vw] overflow-y-auto p-6 pt-24"
              style={{ background: "#FFFFFF" }}
            >
              <div className="flex flex-col gap-1">
                {mobileLinks.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="rounded-xl px-4 py-3 text-sm hover:bg-black/[0.03]"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  {user ? (
                    <>
                      <Button asChild variant="outline" className="w-full justify-start">
                        <Link to="/app" onClick={() => setOpen(false)}>
                          <LayoutDashboardIcon className="mr-2 h-4 w-4" />
                          Dashboard
                        </Link>
                      </Button>
                      <Button variant="ghost" onClick={handleSignOut} className="w-full justify-start text-destructive hover:text-destructive">
                        <LogOutIcon className="mr-2 h-4 w-4" />
                        Sign out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/auth" onClick={() => setOpen(false)}>Sign in</Link>
                      </Button>
                      <a href="#audit" onClick={() => setOpen(false)} className="inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-semibold transition-all hover:opacity-90" style={{ background: "#0C4A6E", color: "#F0F9FF" }}>
                        Request audit
                      </a>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}

export function Footer({
  brandName = brand.name,
  city = brand.city,
  country = brand.country,
  company = brand.company,
  domain = brand.domain,
  logoUrl = null,
}: {
  brandName?: string;
  tagline?: string;
  descriptor?: string;
  city?: string;
  country?: string;
  company?: string;
  domain?: string;
  logoUrl?: string | null;
} = {}) {
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden" style={{ background: FOREST_DEEP, fontFamily: FONT_DISPLAY }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${FOREST_MID}, ${GOLD}40, ${FOREST_MID}, transparent)` }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 50% 100% at 50% 100%, ${GOLD}06 0%, transparent 70%)` }} />

      <div className="relative mx-auto max-w-6xl px-6 py-10 sm:py-12">
        <div className="flex flex-col items-center text-center">
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE_FOOTER }} className="relative">
            <Link to="/" className="relative flex items-center gap-2.5">
              <BrandMark size="md" logoUrl={logoUrl} />
              <span className="text-xl font-bold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.03em" }}>{brandName}</span>
            </Link>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.5 }} className="mt-4 text-sm font-medium" style={{ color: `${GOLD}99`, letterSpacing: "-0.01em" }}>
            The operating infrastructure behind every school.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.4 }} className="mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: `${FOREST_MID}30`, background: `${FOREST_MID}08` }}>
            <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="h-1 w-1 rounded-full" style={{ background: "#4ADE80" }} />
            <span className="text-[9px] font-medium uppercase tracking-[0.15em]" style={{ color: "rgba(255,255,255,0.4)" }}>All systems operational</span>
          </motion.div>
        </div>

        <div className="mt-6 h-px w-full" style={{ background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)` }} />

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.5 }} className="mt-4 flex flex-col items-center justify-between gap-2 sm:flex-row">
          <div className="text-[9px] font-medium" style={{ color: "rgba(255,255,255,0.25)" }}>
            BRECCS Zimbabwe — Business, Records, Education, Commerce, Care &amp; Services.
          </div>
          <div className="text-[9px]" style={{ color: "rgba(255,255,255,0.25)" }}>
            © {year} BRECCS Zimbabwe
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
